import {
  useState, useLayoutEffect, useRef, useMemo,
} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {
  Flex, Button, TextInput, useStyledTheme, FlexColumn, FlexGrow, useRematchDispatch, useUser,
} from '@equifinance/shared';
import { DateFormatStringEnum, initDate, formatDateTime } from '@equifinance/shared/utils/datetime';
import { RootState } from '../../../store';

type Message = {
  userId: string,
  timestamp: string,
  message: string,
}

const ChatMessageContainer = styled(Flex) <{ isCurrentUser: boolean }>`
  justify-content:${(props) => (props.isCurrentUser ? 'flex-end' : 'flex-start')};
`;

const Container = styled(FlexColumn)`
  height:100%;
`;
const ChatMessage = styled.div`${({ theme }) => `
  border-radius: ${theme.borderRadius[1]};
  background: ${theme.secondary};
  padding: ${theme.padding[2]};
`}`;

const InputContainer = styled(Flex)`
  align-items:center;
`;
const MessagesContainer = styled(FlexGrow)`${({ theme }) => `
  overflow-y: scroll;
  margin-bottom: ${theme.padding[2]};
`}`;

export default function Chat({ chatId = '' }: { chatId?: string }) {
  const dispatch = useRematchDispatch();
  const chat = useSelector((rootState: RootState) => rootState.chats[chatId]);
  const { user } = useUser();
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [messageInput, setMessageInput] = useState('');
  const theme = useStyledTheme();

  const chatMessages = useMemo(() => JSON.parse(chat.data).map((message: Message) => (
    <ChatMessageContainer isCurrentUser={user?.id === message.userId}>
      <FlexColumn>
        <ChatMessage>{message.message}</ChatMessage>
        <p>{formatDateTime(message.timestamp, DateFormatStringEnum.DDMMYYYYhmm)}</p>
      </FlexColumn>
    </ChatMessageContainer>
  )), [chat]);

  const onClickSend = () => {
    if (messageInput) {
      dispatch.chats.UPDATE({
        data: JSON.stringify([
          ...JSON.parse(chat.data),
          {
            userId: user?.id,
            timestamp: initDate(),
            message: messageInput,
          },
        ]),
      }, chatId);

      setMessageInput('');
    }
  };

  const inputStyle = `
    padding:0;
    margin-right: ${theme.padding[3]};
  `;

  useLayoutEffect(() => {
    if (messagesEndRef.current !== null) {
      messagesEndRef.current.scrollIntoView();
    }
  }, [chat]);

  return (
    <Container>
      <MessagesContainer>
        {chat && chatMessages}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      <InputContainer>
        <TextInput styles={inputStyle} id="chat-input" placeholder="Enter Message" value={messageInput} onChange={setMessageInput} />
        <Button onClick={onClickSend}>Send</Button>
      </InputContainer>
    </Container>
  );
}
